import { getAuthHeaders } from '../pages/assets';

export interface OfflineRequest {
  id: string;
  url: string;
  method: string;
  body: string;
  headers: Record<string, string>;
  timestamp: number;
  tempSessionTag?: string;
}

const QUARANTINED_SESSION_REQUESTS_KEY = 'opm_quarantined_session_requests';

function isSessionLifecycleRequest(url: string): boolean {
  return /\/tickets\/[^/]+\/sessions\/(start|[^/]+\/(stop|finish|closeout))$/.test(url);
}

function quarantineSessionRequests(requests: OfflineRequest[]): void {
  if (requests.length === 0) return;
  const existing: OfflineRequest[] = JSON.parse(
    localStorage.getItem(QUARANTINED_SESSION_REQUESTS_KEY) || '[]',
  );
  localStorage.setItem(
    QUARANTINED_SESSION_REQUESTS_KEY,
    JSON.stringify([...existing, ...requests]),
  );
}

export function savePendingRequest(
  url: string,
  method: string,
  body: any,
  headers: any,
  tempSessionTag?: string
) {
  const queue: OfflineRequest[] = JSON.parse(localStorage.getItem('opm_offline_queue') || '[]');
  queue.push({
    id: Math.random().toString(36).substring(2),
    url,
    method,
    body: typeof body === 'string' ? body : JSON.stringify(body),
    headers,
    timestamp: Date.now(),
    tempSessionTag
  });
  localStorage.setItem('opm_offline_queue', JSON.stringify(queue));
}

export async function processOfflineQueue(): Promise<boolean> {
  const storedQueue: OfflineRequest[] = JSON.parse(localStorage.getItem('opm_offline_queue') || '[]');
  const staleSessionRequests = storedQueue.filter((request) => isSessionLifecycleRequest(request.url));
  quarantineSessionRequests(staleSessionRequests);
  const queue = storedQueue.filter((request) => !isSessionLifecycleRequest(request.url));
  localStorage.setItem('opm_offline_queue', JSON.stringify(queue));
  if (queue.length === 0) return true;
  if (!navigator.onLine) return false;

  let success = true;
  const tempSessionMap: Record<string, string> = {};
  const currentAuth = getAuthHeaders();

  let failedIndex = -1;
  for (let idx = 0; idx < queue.length; idx++) {
    const req = queue[idx];
    if (!req) continue;
    let url = req.url;
    // Replace temporary session tags in URL
    for (const [tempTag, realId] of Object.entries(tempSessionMap)) {
      url = url.replace(tempTag, realId);
    }

    // Merge in the latest auth header (in case token refreshed)
    const headers = { ...req.headers };
    if (headers.Authorization && currentAuth.Authorization) {
      headers.Authorization = currentAuth.Authorization;
    }

    let body = req.body;
    // Also replace temporary session tags in body if needed
    for (const [tempTag, realId] of Object.entries(tempSessionMap)) {
      if (body) {
        body = body.replace(new RegExp(tempTag, 'g'), realId);
      }
    }

    try {
      const res = await fetch(url, {
        method: req.method,
        headers,
        body
      });

      if (res.ok) {
        if (req.tempSessionTag && url.endsWith('/sessions/start')) {
          const data = await res.json();
          if (data && data.id) {
            tempSessionMap[req.tempSessionTag] = data.id;
          }
        }
      } else {
        failedIndex = idx;
        success = false;
        break;
      }
    } catch (err) {
      failedIndex = idx;
      success = false;
      break;
    }
  }

  // The remaining queue is the failed request plus any untried requests
  const nextQueue = !success && failedIndex !== -1 ? queue.slice(failedIndex) : [];
  localStorage.setItem('opm_offline_queue', JSON.stringify(nextQueue));
  return success;
}
